import type {
  GraphemeMorphemeMnemonicsPanelMorphemeSlice,
  GraphemeMorphemeMnemonicsPanelMorphemeUserSlice,
} from "@-/dev.improve.haniverse.lib.morpheme.entities";
import type { MorphemeHrefObject } from "@-/dev.improve.haniverse.lib.morpheme.href.entities";
import type { ResolvedQuery } from "@-/dev.improve.haniverse.lib.query-options";
import { GraphemePreviewSheetProvider } from "@-/dev.improve.haniverse.routes.nav.app._components.morpheme.panels._components.preview-sheet";
import {
  GraphemeMorphemePanelsContextProvider,
  MorphemePanelsContextProvider,
} from "@-/dev.improve.haniverse.routes.nav.app._components.morpheme.panels._context";
import { MorphemeMnemonicsIntroducedReadingsContext } from "@-/dev.improve.haniverse.routes.nav.app._components.morpheme.panels.tab-host-context";
import { type Dispatch, type ReactNode, type RefObject, type SetStateAction, useMemo } from "react";
import type { MorphemeMnemonicsViewNavigationRefValue } from "#morpheme-panels";

/**
 * The providers a grapheme's mnemonics and Info surfaces read when they are rendered OUTSIDE the
 * tabbed panels navigator — on a Learn deck card. The mnemonics navigation props are the caller's
 * (a lesson steps its pronunciation tabs from its action area; a review card passes inert
 * values). `introducedPinyinList` is the caller's too: the readings the card treats as introduced,
 * whose pinyin its Learn binder tab colours. Mnemonic links and decomposition components preview the linked grapheme in a sheet
 * instead of navigating, so a reader can check a component without losing the card.
 */
export function MorphemePanelsFlashcardProviders({
  activeIndex,
  character,
  children,
  introducedPinyinList,
  mnemonicsSlice,
  mnemonicsUserSlice,
  morphemeHrefObject,
  setActiveIndex,
  viewNavigationRef,
}: {
  activeIndex: number;
  character: string;
  children: ReactNode;
  introducedPinyinList: readonly string[];
  /** Narrowed by the caller's gate (the Learn card that already rendered its not-found state). */
  mnemonicsSlice: ResolvedQuery<GraphemeMorphemeMnemonicsPanelMorphemeSlice | null>;
  mnemonicsUserSlice: ResolvedQuery<GraphemeMorphemeMnemonicsPanelMorphemeUserSlice>;
  morphemeHrefObject: MorphemeHrefObject;
  setActiveIndex: Dispatch<SetStateAction<number>>;
  viewNavigationRef: RefObject<MorphemeMnemonicsViewNavigationRefValue | null>;
}) {
  const introducedPinyinSet = useMemo(() => new Set(introducedPinyinList), [introducedPinyinList]);
  return (
    <MorphemePanelsContextProvider
      activeIndex={activeIndex}
      character={character}
      morphemeHrefObject={morphemeHrefObject}
      setActiveIndex={setActiveIndex}
      viewNavigationRef={viewNavigationRef}
    >
      <GraphemeMorphemePanelsContextProvider
        readonlySlice={mnemonicsSlice}
        userSlice={mnemonicsUserSlice}
      >
        <MorphemeMnemonicsIntroducedReadingsContext.Provider value={introducedPinyinSet}>
          <GraphemePreviewSheetProvider morphemeHrefObject={morphemeHrefObject}>
            {children}
          </GraphemePreviewSheetProvider>
        </MorphemeMnemonicsIntroducedReadingsContext.Provider>
      </GraphemeMorphemePanelsContextProvider>
    </MorphemePanelsContextProvider>
  );
}
